import CategoryMasterService from "@/services/api/categoryMaster";
import { handleResponse } from "@/services/axios";
import { saveAsExcelFile } from "@/services/excelService/excelSevice";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

interface ICategoryMasterState {
  categories: any[];
  childCategories: any[];
  loading: boolean;
  allCategoriesSampleFile: any;
  categoriesReport: { Items?: any[]; TotalCount?: number };
}

export const fetchCategoriesReport = createAsyncThunk(
  "categoryMaster/fetchCategoriesReport",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response =
        await CategoryMasterService.fetchCategoriesReport(payload);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error?.Message ?? "Failed to fetch categories report data",
      );
    }
  },
);

export const fetchCategories = createAsyncThunk(
  "categoryMaster/fetchCategories",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await CategoryMasterService.fetchCategories(payload);
      const result = handleResponse(response, rejectWithValue);
      return result;
    } catch (error: any) {
      return rejectWithValue(
        error?.Message ?? "Failed to fetch categories data",
      );
    }
  },
);

export const fetchChildCategories = createAsyncThunk(
  "categoryMaster/fetchChildCategories",
  async (_, { rejectWithValue }) => {
    try {
      const response = await CategoryMasterService.fetchChildCategories();
      const result = handleResponse(response, rejectWithValue);
      return result;
    } catch (error: any) {
      return rejectWithValue(
        error?.Message ?? "Failed to fetch child categories data",
      );
    }
  },
);


export const addNewCategory = createAsyncThunk(
  "categoryMaster/addNewCategory",
  async (payload: any, { rejectWithValue }) => {
    try {
      const res = await CategoryMasterService.addNewCategory(payload);
      const result = handleResponse(res, rejectWithValue);
      return result;
    } catch (err: any) {
      return rejectWithValue(err?.Message);
    }
  },
);

export const updateCategory = createAsyncThunk(
  "categoryMaster/updateCategory",
  async (payload: any, { rejectWithValue }) => {
    try {
      const res = await CategoryMasterService.updateCategory(payload);
      const result = handleResponse(res, rejectWithValue);
      return result;
    } catch (err: any) {
      return rejectWithValue(err?.Message);
    }
  },
);

export const deleteCategory = createAsyncThunk(
  "categoryMaster/deleteCategory",
  async (id: number, { rejectWithValue, dispatch }) => {
    try {
      const response = await CategoryMasterService.deleteCategory(id);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to delete Category");
    }
  },
);

export const downloadAllCategories = createAsyncThunk(
  "categoryMaster/downloadAllCategories",
  async (_, { rejectWithValue }) => {
    try {
      const response = await CategoryMasterService.downloadAllCategories();
      return saveAsExcelFile(response, "Categories");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message ?? "Failed to download All Categories",
      );
    }
  },
);

export const categoryBulkUpload = createAsyncThunk(
  "categoryMaster/categoryBulkUpload",
  async (payload: any, { rejectWithValue }) => {
    try {
      const res = await CategoryMasterService.categoryBulkUpload(payload);
      return res;
    } catch (err: any) {
      return rejectWithValue(err?.Message);
    }
  },
);

export const exportBulkUploadReport = createAsyncThunk(
  "categoryMaster/exportBulkUploadReport",
  async (fileId: string, { rejectWithValue }) => {
    try {
      const response =
        await CategoryMasterService.exportBulkUploadReport(fileId);
      return saveAsExcelFile(response, "Category_BulkUpload");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message ?? "Failed to export bulk upload report",
      );
    }
  },
);

export const exportBulkUploadFile = createAsyncThunk(
  "categoryMaster/exportBulkUploadFile",
  async (fileId: string, { rejectWithValue }) => {
    try {
      const response = await CategoryMasterService.exportBulkUploadFile(fileId);
      return saveAsExcelFile(response, "CategoryBulkUploadFileReport"); 
    } catch (error: any) {
      return rejectWithValue(
        error?.Message ?? "Failed to export bulk upload file",
      );
    }
  },
);

export const fetchBulkUploadViewReports = createAsyncThunk(
  "categoryMaster/fetchBulkUploadViewReports",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response =
        await CategoryMasterService.fetchBulkUploadViewReports(payload);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error?.Message ?? "Failed to fetch bulk upload view reports data",
      );
    }
  },
);

const initialState: ICategoryMasterState = {
  categories: [],
  childCategories: [],
  loading: false,
  allCategoriesSampleFile: null,
  categoriesReport: {},
};

const categoryMasterSlice = createSlice({
  name: "categoryMaster",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload as any[];
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(fetchChildCategories.fulfilled, (state, action) => {
        state.childCategories = action.payload;
      })
      .addCase(downloadAllCategories.fulfilled, (state, action) => {
        state.allCategoriesSampleFile = action.payload;
      })
      .addCase(fetchCategoriesReport.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCategoriesReport.fulfilled, (state, action) => {
        state.categoriesReport = action.payload;
        state.loading = false;
      })
      .addCase(fetchCategoriesReport.rejected, (state, action) => {
        state.loading = false;
      });
  },
});

export const {} = categoryMasterSlice.actions;
export default categoryMasterSlice.reducer;
